import type {PropertyAccess} from "@/types/Idea";
import {isMediaType} from "@/utils/mediaTypes";

export type PropertyRule = { rule: string, message?: string, dependsOn?: string[] }

/**
 * O valor com que o campo nasce no formulário de criar (pedido do usuário): fixo (`value`), na forma do
 * valor do item — o id da opção ou do item, a lista no múltiplo —, ou por fórmula (`formula`), que o
 * servidor avalia na prévia e que se refaz enquanto ninguém mexe no campo. A chave diz qual dos dois.
 *
 * Mora na PROPRIEDADE, e não na opção do domínio: o domínio é do ambiente e pode ser compartilhado por
 * atributos de coleções diferentes — marcar a opção amarraria todos eles à mesma escolha.
 */
export type PropertyDefault = { value: any } | { formula: string }

/** Quem tem padrão: a propriedade, ou o modelo da janela, que guarda o "nenhum" como `null`. */
type WithDefault = { default?: PropertyDefault | null }

/** A fórmula do padrão, quando ele é por fórmula. */
export function defaultFormulaOf(property: WithDefault): string | undefined {
    return property.default && 'formula' in property.default ? property.default.formula : undefined
}

/** O padrão fixo, ou `undefined` quando não há — ou quando ele é por fórmula. */
export function defaultValueOf(property: WithDefault): any {
    return property.default && 'value' in property.default ? property.default.value : undefined
}

/**
 * Pode ter padrão? Só o campo que alguém preenche e que mora no item: nem a calculada de sempre (o valor já
 * vem da fórmula), nem o reverso (é a lista do outro lado), nem a mídia (um arquivo enviado). O "Calculado
 * quando" pode: o item que nasce preenchido nasce com ele. O par do `defaultable` do servidor.
 */
export function defaultable(property: Pick<IdeaProperty, 'formula' | 'computedWhen' | 'relationship' | 'type'>): boolean {
    return !alwaysComputed(property) && !property.relationship?.reverse && !isMediaType(property)
}

/**
 * Calculado SEMPRE: ninguém o preenche, e ele não mora no item. O "Calculado quando" não — ele é gravado,
 * e o cálculo só se sobrepõe enquanto a condição dá Sim (`computingNow`). O par do `alwaysComputed` do
 * servidor.
 */
export function alwaysComputed(property: Pick<IdeaProperty, 'formula' | 'computedWhen'>): boolean {
    return !!property.formula && !property.computedWhen
}

/**
 * Alguém preenche este campo — sempre, ou enquanto a condição do "Calculado quando" dá Não? O que é de quem
 * preenche — obrigatório, exclusivo, regras, opções permitidas, padrão, o mínimo e o máximo, compor o nome
 * cobrado como obrigatório — vale para ele (pedido do usuário: no "Calculado quando" isso sumia, e ele é
 * preenchido boa parte do tempo). Só a calculada de sempre não tem.
 *
 * Da propriedade gravada (`formula`) ou do rascunho do editor (`computed`), em que o "quando" ainda sem
 * fórmula é o texto vazio — e já é "quando". Um dono para a janela da propriedade e o design da coleção,
 * que perguntavam `!model.computed` em cada lugar.
 */
export function fillable(property: { computed?: boolean, formula?: string, computedWhen?: string }): boolean {
    return !(property.computed || property.formula) || property.computedWhen !== undefined
}

/**
 * O campo está sendo calculado NESTE item — só leitura, e fora do que se grava? O calculado de sempre,
 * sim; o "Calculado quando", quando o item o traz em `_computing` (da leitura e da prévia: quem avalia a
 * condição é o servidor). Um dono para o formulário, o salvar e a validação, que liam `.formula`.
 */
export function computingNow(item: Record<string, any>, property: Pick<IdeaProperty, 'alias' | 'formula' | 'computedWhen'>): boolean {
    return alwaysComputed(property)
        || (!!property.computedWhen && Array.isArray(item._computing) && item._computing.includes(property.alias))
}

/**
 * Põe no item o que a prévia diz dos calculados: os que estão sendo calculados agora (`computing`) e os
 * valores deles (`computed`). É a troca da sobreposição do "Calculado quando" (decisão do usuário): ao
 * ligar, o preenchido vai para `_stored` — mesmo o digitado e ainda não gravado —, e ao desligar volta ao
 * campo. O item que abriu já calculado trouxe o `_stored` da leitura.
 */
export function applyComputing(instance: Record<string, any>, properties: IdeaProperty[],
                               {computing, computed}: { computing: string[], computed: Record<string, any> }) {
    const before: string[] = Array.isArray(instance._computing) ? instance._computing : []
    const stored: Record<string, any> = {...(instance._stored || {})}
    for (const property of properties.filter(candidate => candidate.computedWhen)) {
        const alias = property.alias
        if (computing.includes(alias) && !before.includes(alias)) stored[alias] = instance[alias]
        if (!computing.includes(alias) && before.includes(alias)) {
            instance[alias] = stored[alias]
            delete stored[alias]
        }
    }
    instance._computing = computing
    instance._stored = stored
    for (const [alias, value] of Object.entries(computed)) instance[alias] = value === null ? undefined : value
}

type IdeaProperty = {
    name: string
    alias: string
    type?: string
    subtype?: string
    /** Sempre obrigatório. O obrigatório só às vezes é o `requiredWhen`, à parte. */
    required?: boolean
    /**
     * "Obrigatório quando" (pedido do usuário): uma fórmula de Sim/Não que diz quando o campo é obrigatório
     * — "fim, quando a situação é Concluído". Na lista, o mínimo de itens só vale quando ela dá Sim. Quem a
     * avalia é o servidor: a leitura e a prévia dizem quais campos estão obrigatórios agora (`_required`).
     */
    requiredWhen?: string
    /** Os campos do item que mudam o "Obrigatório quando" — derivado pelo servidor, como o `dependsOn`. */
    requiredDependsOn?: string[]
    unique?: boolean
    multiple?: boolean
    identifier?: boolean
    relationship?: {reverse?: string , idea: string }
    /** Alias do domínio, quando `type` é `choice`. */
    domain?: string
    /** O valor com que o campo nasce, fixo ou por fórmula — ver `PropertyDefault`. Ausente é o normal. */
    default?: PropertyDefault
    /**
     * Os campos do item que refazem o padrão por fórmula — derivado pelo servidor, como o `dependsOn`.
     * O formulário pede a prévia quando muda um deles, enquanto ninguém mexeu no campo do padrão.
     */
    defaultDependsOn?: string[]
    /**
     * A fórmula de um atributo calculado, em CEL — e quem a calcula é o SERVIDOR, a cada leitura.
     *
     * O cliente não executa nada do que o designer escreve: o `script` de antes era JavaScript
     * executado aqui com a sessão de quem abria a tela. O valor calculado chega pronto, como
     * qualquer outro campo, e no formulário ele vem da prévia do servidor.
     */
    formula?: string
    /**
     * "Calculado quando" (pedido do usuário): a fórmula de Sim/Não que diz quando o `formula` vale. Com ela
     * o campo é gravado como qualquer outro, e enquanto ela dá Sim o cálculo se sobrepõe ao preenchido, que
     * volta quando ela dá Não. Só em atributo. Ver `computingNow`.
     */
    computedWhen?: string
    /**
     * Os campos do item que mudam o valor desta calculada — derivado da fórmula pelo servidor,
     * a cada entrega da coleção; ninguém o escreve e ele não é gravado. O formulário pede a
     * prévia só quando muda um destes, e manda só eles. Ver `designedProperty`.
     */
    dependsOn?: string[]
    /**
     * No atributo calculado, o que a fórmula lê e o nome dos itens não alcança (`currentUser`,
     * `cidade.populacao`) — o que o impede de compor o nome. Derivado pelo servidor, como o `dependsOn`:
     * o cliente não lê fórmula. Ver `identifierBlocker`.
     */
    nameBlocker?: string
    /**
     * As regras de validação: fórmulas que têm de dar Sim, com a mensagem de quem as escreveu (sem
     * ela, a frase padrão). Quem confere é o servidor — o cliente não avalia fórmula. O `dependsOn`
     * de cada uma é derivado pelo servidor, como o da calculada, e não se grava.
     */
    rules?: PropertyRule[]
    /**
     * As "Opções permitidas" de um relacionamento ou seleção: a fórmula julgada para CADA opção, com
     * `self` sendo ela. O formulário só oferece as que passam — quem julga é o servidor, pela prévia
     * (`allowed`) —, e a gravação recusa a que entrar sem passar. À parte das regras (decisão do
     * usuário): a regra valida o que se escolheu; o filtro diz o que se pode escolher.
     */
    optionFilter?: string
    /**
     * A orientação do filtro, opcional: embaixo do campo, como dica (e não erro), e a frase da recusa
     * quando uma opção que não passa chega por outro caminho — o arrasto no Kanban.
     */
    optionFilterMessage?: string
    /** Os campos do item que mudam o que o filtro oferece — derivado pelo servidor, como o `dependsOn`. */
    optionFilterDependsOn?: string[]
    /**
     * Quem vê e quem edita o campo, por fórmula (pedido do usuário) — como o `access` da coleção diz do
     * item. Ausente, quem vê o item vê e edita o campo. Quem julga é o servidor: a leitura diz quais
     * campos do item estão ocultos ou travados para quem pede (`fieldVisible`/`fieldEditable`).
     */
    access?: PropertyAccess
    itemCount?: {min: number, max: number}
}

export default IdeaProperty